import React, { useEffect, useRef, useState } from 'react';
import HoverableSelector from 'src/molecules/Standard/Shared/2024_Q3_profit_sales_redesign/HoverableSelector/HoverableSelector';
import Heading from 'src/elements/typography/heading';
import useOnViewport from 'src/hooks/useOnViewport';
import useHoverableCardsSection from './useHoverableCardsSection';
import ArrowWithCircle from 'src/images/2024_Q1_people_magic/ArrowWithCircle';
import {
  ArrowButton,
  ArrowsContainer,
  HoverableCardsSectionContainer,
  HoverableCardsSectionContent,
  ItemsContainer,
} from './HoverableCardsSection.styles';
import Paragraph from 'src/elements/typography/paragraph';
import HoverableCardsSectionItems from 'src/molecules/Shared/HoverableCardsSectionItems/HoverableCardsSectionItems';

interface HoverableCardsSectionProps {
  title: string;
  subtitle: string;
}

const SELECTOR_BACKGROUND_COLOR =
  'linear-gradient(90deg, rgba(255, 255, 255, 0.10) 15.49%, rgba(255, 255, 255, 0.50) 24.88%, rgba(255, 255, 255, 0.25) 29.33%, rgba(255, 255, 255, 0.10) 55.03%)';

const HoverableCardsSection = ({ title, subtitle }: HoverableCardsSectionProps): JSX.Element => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const items = useHoverableCardsSection();

  const selectionItems = items.map((item, index) => ({
    text: item.section,
    active: index === activeItemIndex,
  }));

  const isOnViewport = useOnViewport(containerRef);

  useEffect(() => {
    if (!isOnViewport) return;

    const timeout = setTimeout(() => {
      setActiveItemIndex((activeItemIndex + 1) % items.length);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [activeItemIndex, isOnViewport]);

  const goNext = () => setActiveItemIndex((prev) => (prev + 1) % items.length);
  const goPrev = () => setActiveItemIndex((prev) => (prev - 1 + items.length) % items.length);

  const renderArrowWithCircle = (orientation: 'left' | 'right') => (
    <ArrowButton
      orientation={orientation}
      onClick={orientation === 'left' ? goPrev : goNext}
    >
      <ArrowWithCircle
        width={60}
        color='greyText'
      />
    </ArrowButton>
  );

  return (
    <HoverableCardsSectionContainer ref={containerRef}>
      <HoverableCardsSectionContent>
        <Heading
          text={title}
          standardSize='3.375'
          align='center'
        />
        <Paragraph
          align='center'
          standardSize='1.5'
          text={subtitle}
        />
        <HoverableSelector
          items={selectionItems}
          onItemClick={setActiveItemIndex}
          backgroundColor={() => SELECTOR_BACKGROUND_COLOR}
          borderColor='white'
          textColor='greyText'
          activeColor='cream'
        />
        <ItemsContainer>
          {items.map((item, index) => (
            <HoverableCardsSectionItems
              key={item.section}
              items={item.items}
              active={activeItemIndex === index}
            />
          ))}
        </ItemsContainer>
        <ArrowsContainer>
          {renderArrowWithCircle('left')}
          {renderArrowWithCircle('right')}
        </ArrowsContainer>
      </HoverableCardsSectionContent>
    </HoverableCardsSectionContainer>
  );
};

export default HoverableCardsSection;
